import { useState, useEffect } from "react";import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import {
  Sparkles,
  Plus,
  Play,
  Pause,
  Rocket,
  Trash2,
  Mail,
  RefreshCcw,
  Wand2,
  Clock,
  Loader2,
  ChevronsUpDown,
  X as XIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  active: "bg-green-100 text-green-700",
  paused: "bg-amber-100 text-amber-700",
  archived: "bg-slate-100 text-slate-500",
};

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/campaigns`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in-up" data-testid="campaigns-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-blue-600" />
            AI Sales Campaigns
          </h1>
          <p className="text-slate-500 mt-1">
            Drip email sequences on autopilot. AI drafts, you approve, then runs hands-off.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchCampaigns}>
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setWizardOpen(true)} data-testid="new-campaign-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Sparkles className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No campaigns yet. Create your first AI drip sequence.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {campaigns.map((c) => (
                <div
                  key={c.campaign_id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer"
                  onClick={() => setSelected(c)}
                  data-testid={`campaign-row-${c.campaign_id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{c.name}</span>
                      <Badge className={`${STATUS_COLORS[c.status] || ""} text-xs`}>{c.status}</Badge>
                      <span className="text-xs text-slate-500">{c.steps?.length} steps</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate max-w-xl">{c.description || c.goal}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="text-right">
                      <div className="text-slate-600">Enrolled</div>
                      <div className="font-semibold text-slate-900">{c.stats?.enrolled || 0}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-600">Sent</div>
                      <div className="font-semibold text-slate-900">{c.stats?.sent || 0}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-600">Unsub</div>
                      <div className="font-semibold text-slate-900">{c.stats?.unsubscribed || 0}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CampaignWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => { setWizardOpen(false); fetchCampaigns(); }}
      />

      {selected && (
        <CampaignDetail
          campaign={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { fetchCampaigns(); setSelected(null); }}
        />
      )}
    </div>
  );
}

function CampaignWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1 = goal, 2 = review, 3 = launch
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    goal: "",
    audience: "",
    sequence_length: 5,
    tone: "friendly-professional",
  });
  const [sequence, setSequence] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setForm({ name: "", goal: "", audience: "", sequence_length: 5, tone: "friendly-professional" });
      setSequence([]);
    }
  }, [open]);

  const handleGenerate = async () => {
    if (!form.goal.trim()) {
      toast.error("Describe your campaign goal");
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/campaigns/generate-sequence`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setSequence(data.steps || []);
        if (!form.name && data.sequence_name) setForm({ ...form, name: data.sequence_name });
        setStep(2);
        toast.success("AI drafted your sequence. Review & edit.");
      } else {
        toast.error(data.detail || "Generation failed", { duration: 8000 });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Campaign name is required");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/campaigns`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          goal: form.goal,
          audience: form.audience,
          steps: sequence.map((s, i) => ({ ...s, order: i + 1 })),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Campaign saved as draft. Launch it to enroll leads.");
        onCreated();
      } else {
        toast.error(data.detail || "Save failed");
      }
    } finally {
      setCreating(false);
    }
  };

  const updateStep = (idx, field, val) => {
    const copy = [...sequence];
    copy[idx] = { ...copy[idx], [field]: val };
    setSequence(copy);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="campaign-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 1 ? <Wand2 className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
            {step === 1 ? "Describe your campaign" : "Review AI-drafted sequence"}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Tell the AI what you want to achieve. It will draft a multi-step email sequence."
              : "Edit each email inline. Merge tags like {{business_name}} are auto-filled per lead."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Goal *</Label>
              <Textarea
                placeholder="e.g. Book 15-minute demos with dental practice owners in California who want to reduce no-shows"
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                rows={3}
                data-testid="campaign-goal-input"
              />
            </div>
            <div>
              <Label>Audience Description (optional)</Label>
              <Textarea
                placeholder="e.g. 10-50 employees, owner-operated, already uses appointment software"
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                rows={2}
                data-testid="campaign-audience-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Number of Emails</Label>
                <Input
                  type="number"
                  min="3"
                  max="7"
                  value={form.sequence_length}
                  onChange={(e) => setForm({ ...form, sequence_length: parseInt(e.target.value) || 5 })}
                />
              </div>
              <div>
                <Label>Tone</Label>
                <select
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  value={form.tone}
                  onChange={(e) => setForm({ ...form, tone: e.target.value })}
                >
                  <option value="friendly-professional">Friendly &amp; Professional</option>
                  <option value="direct">Direct &amp; Concise</option>
                  <option value="warm">Warm &amp; Personal</option>
                  <option value="consultative">Consultative</option>
                  <option value="witty">Witty &amp; Casual</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
            </div>
            {sequence.map((s, i) => (
              <Card key={i} className="border-slate-200">
                <CardHeader className="pb-2 flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold">
                        {i + 1}
                      </span>
                      Email {i + 1} — {s.purpose || "step"}
                    </CardTitle>
                  </div>
                  <Badge variant="outline" className="text-xs gap-1">
                    <Clock className="w-3 h-3" />
                    {s.delay_days === 0 ? "sends immediately" : `+${s.delay_days} days`}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Input
                    value={s.subject || ""}
                    onChange={(e) => updateStep(i, "subject", e.target.value)}
                    placeholder="Subject"
                    data-testid={`step-${i}-subject`}
                  />
                  <Textarea
                    rows={6}
                    value={s.body || ""}
                    onChange={(e) => updateStep(i, "body", e.target.value)}
                    placeholder="Body (use {{business_name}}, {{city}}, {{contact_name}} merge tags)"
                    data-testid={`step-${i}-body`}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <DialogFooter className="flex-row gap-2 justify-between">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            {step === 2 && (
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
            )}
            {step === 1 ? (
              <Button onClick={handleGenerate} disabled={generating} data-testid="generate-sequence-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {generating ? "Drafting..." : "Generate Sequence"}
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={creating} data-testid="save-campaign-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save as Draft
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignDetail({ campaign, onClose, onChanged }) {
  const [launching, setLaunching] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCategories, setFilterCategories] = useState([]);
  const [audienceCount, setAudienceCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    fetch(`${API}/leads/categories`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setCategoryOptions(data?.categories || []))
      .catch(() => {});
  }, []);

  // ── Draft edit state ────────────────────────────────────────────
  const isDraft = campaign.status === "draft";
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editName, setEditName] = useState(campaign.name || "");
  const [editDescription, setEditDescription] = useState(campaign.description || "");
  const [editSteps, setEditSteps] = useState(
    (campaign.steps || []).map((s) => ({
      order: s.order,
      delay_days: s.delay_days || 0,
      subject: s.subject || "",
      body: s.body || "",
      purpose: s.purpose || "",
    })),
  );
  const [bookingUrl, setBookingUrl] = useState("");

  useEffect(() => {
    // Pull the rep's booking URL once, only when entering edit mode (so we can show "Insert booking link")
    if (!editing || bookingUrl) return;
    fetch(`${API.replace("/api", "")}/api/booking/availability`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.booking_slug) {
          const base = window.location.origin;
          setBookingUrl(`${base}/book/${data.booking_slug}`);
        }
      })
      .catch(() => {});
  }, [editing, bookingUrl]);

  const updateStep = (idx, patch) => {
    setEditSteps((arr) => arr.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const insertBookingTag = (idx) => {
    setEditSteps((arr) =>
      arr.map((s, i) => {
        if (i !== idx) return s;
        const sep = s.body && !s.body.endsWith("\n") ? "\n\n" : "";
        return { ...s, body: `${s.body}${sep}Grab a slot here: {{booking_link}}` };
      }),
    );
    toast.success("Booking link merge tag inserted");
  };

  const saveDraft = async () => {
    // Lightweight validation
    for (const s of editSteps) {
      if (!s.subject.trim()) {
        toast.error(`Step ${s.order}: subject is required`);
        return;
      }
      if (!s.body.trim()) {
        toast.error(`Step ${s.order}: body is required`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/campaigns/${campaign.campaign_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editName,
          description: editDescription,
          steps: editSteps,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      toast.success("Draft saved");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const previewAudience = async () => {
    setCounting(true);
    setAudienceCount(null);
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== "any") params.set("status", filterStatus);
      if (filterCity) params.set("city", filterCity);
      if (filterState) params.set("state", filterState);
      if (filterCategories && filterCategories.length > 0) {
        params.set("categories", filterCategories.join(","));
      } else if (filterCategory && filterCategory !== "any") {
        params.set("category", filterCategory);
      }
      const res = await fetch(`${API}/campaigns/audience-count?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAudienceCount(data.count);
      }
    } finally {
      setCounting(false);
    }
  };

  const handleLaunch = async () => {
    if (!window.confirm(`Launch "${campaign.name}" — the AI will start sending emails automatically to matching leads. Continue?`)) return;
    setLaunching(true);
    try {
      const res = await fetch(`${API}/campaigns/${campaign.campaign_id}/launch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audience_filter: {
            ...(filterStatus && filterStatus !== "any" && { status: filterStatus }),
            ...(filterCity && { city: filterCity }),
            ...(filterState && { state: filterState }),
            ...(filterCategories && filterCategories.length > 0
              ? { categories: filterCategories }
              : (filterCategory && filterCategory !== "any" ? { category: filterCategory } : {})),
          },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        onChanged();
      } else {
        toast.error(data.detail || "Launch failed", { duration: 8000 });
      }
    } finally {
      setLaunching(false);
    }
  };

  const handlePause = async () => {
    const res = await fetch(`${API}/campaigns/${campaign.campaign_id}/pause`, { method: "POST", credentials: "include" });
    if (res.ok) { toast.success("Paused"); onChanged(); }
  };

  const handleResume = async () => {
    const res = await fetch(`${API}/campaigns/${campaign.campaign_id}/resume`, { method: "POST", credentials: "include" });
    if (res.ok) { toast.success("Resumed"); onChanged(); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${campaign.name}"? This stops all enrollments and cannot be undone.`)) return;
    const res = await fetch(`${API}/campaigns/${campaign.campaign_id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Deleted"); onChanged(); }
  };

  return (
    <Dialog open={!!campaign} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="campaign-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {editing ? (
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="text-base font-semibold"
                data-testid="edit-campaign-name"
              />
            ) : (
              <span>{campaign.name}</span>
            )}
            <Badge className={STATUS_COLORS[campaign.status] || ""}>{campaign.status}</Badge>
            {isDraft && !editing && (
              <Button
                size="sm"
                variant="outline"
                className="ml-auto"
                onClick={() => setEditing(true)}
                data-testid="edit-draft-btn"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                Edit draft
              </Button>
            )}
          </DialogTitle>
          {editing ? (
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={3}
              placeholder="Describe the campaign goal so reps know its purpose"
              className="mt-2"
              data-testid="edit-campaign-description"
            />
          ) : (
            <DialogDescription>{campaign.description || campaign.goal}</DialogDescription>
          )}
        </DialogHeader>

        <div className="grid grid-cols-4 gap-3 py-3">
          <StatCard label="Steps" value={campaign.steps?.length || 0} />
          <StatCard label="Enrolled" value={campaign.stats?.enrolled || 0} />
          <StatCard label="Sent" value={campaign.stats?.sent || 0} />
          <StatCard label="Unsubscribes" value={campaign.stats?.unsubscribed || 0} />
        </div>

        {campaign.status === "draft" && (
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Rocket className="w-4 h-4" />Launch to Audience</CardTitle>
              <CardDescription>Filter leads, then enroll them. Leave blank to enroll all leads with an email address.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setAudienceCount(null); }}>
                  <SelectTrigger data-testid="launch-filter-status">
                    <SelectValue placeholder="Lead status (any)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any status</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="closed_won">Closed Won</SelectItem>
                    <SelectItem value="closed_lost">Closed Lost</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="City"
                  value={filterCity}
                  onChange={(e) => { setFilterCity(e.target.value); setAudienceCount(null); }}
                  data-testid="launch-filter-city"
                />
                <Input
                  placeholder="State (e.g. CA)"
                  value={filterState}
                  onChange={(e) => { setFilterState(e.target.value); setAudienceCount(null); }}
                  data-testid="launch-filter-state"
                />
              </div>
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                      data-testid="launch-filter-category"
                    >
                      <span className="truncate text-left">
                        {filterCategories.length === 0
                          ? "Lead category (any)"
                          : filterCategories.length === 1
                            ? <span className="capitalize">{filterCategories[0].replace(/_/g, " ")}</span>
                            : `${filterCategories.length} categories selected`}
                      </span>
                      <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
                    <div className="flex items-center justify-between mb-1 px-1">
                      <button
                        type="button"
                        className="text-[11px] text-blue-600 hover:underline"
                        onClick={() => { setFilterCategories([]); setAudienceCount(null); }}
                        data-testid="launch-categories-clear"
                      >
                        Clear all
                      </button>
                      <span className="text-[11px] text-slate-400">{filterCategories.length} selected</span>
                    </div>
                    <div className="max-h-72 overflow-y-auto space-y-0.5">
                      {categoryOptions.length === 0 && (
                        <p className="text-xs text-slate-400 text-center py-3">
                          No categories yet — import or tag leads first.
                        </p>
                      )}
                      {categoryOptions.map((c) => {
                        const checked = filterCategories.includes(c.category);
                        return (
                          <label
                            key={c.category}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                            data-testid={`launch-category-item-${c.category}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                setFilterCategories((prev) =>
                                  v ? [...prev, c.category] : prev.filter((x) => x !== c.category)
                                );
                                setAudienceCount(null);
                              }}
                            />
                            <span className="capitalize text-sm flex-1">{c.category.replace(/_/g, " ")}</span>
                            <span className="text-xs text-slate-400">{c.count}</span>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
                {filterCategories.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="launch-filter-category-chips">
                    {filterCategories.map((c) => (
                      <Badge
                        key={c}
                        variant="secondary"
                        className="capitalize gap-1 pr-1"
                      >
                        {c.replace(/_/g, " ")}
                        <button
                          type="button"
                          className="ml-0.5 rounded-full hover:bg-slate-200 p-0.5"
                          onClick={() => {
                            setFilterCategories((prev) => prev.filter((x) => x !== c));
                            setAudienceCount(null);
                          }}
                          data-testid={`launch-category-remove-${c}`}
                        >
                          <XIcon className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-1">
                  Categories come from your imported leads (e.g. restaurant, college, dentist). Counts show leads with an email on file. Pick one or more.
                </p>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                <strong>Note:</strong> only leads with an email address are enrolled. Leads without an email are skipped.
              </p>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <button
                  type="button"
                  onClick={previewAudience}
                  className="text-blue-600 hover:underline disabled:opacity-50"
                  disabled={counting}
                  data-testid="preview-audience-btn"
                >
                  {counting ? "Counting…" : "Preview match count"}
                </button>
                {audienceCount !== null && (
                  <span data-testid="audience-count" className="text-right">
                    <strong className={audienceCount === 0 ? "text-amber-600" : "text-green-700"}>
                      {audienceCount}
                    </strong>{" "}
                    {audienceCount === 1 ? "lead" : "leads"} will be enrolled
                    {audienceCount > 0 && (
                      <span className="block text-[11px] text-slate-500 mt-0.5" data-testid="send-window-estimate">
                        {(() => {
                          // Sender throttle: 50/hour per rep. Assume continuous send.
                          const perHour = 50;
                          const hours = audienceCount / perHour;
                          if (hours <= 1) return "Should finish in under an hour at 50 emails/hour";
                          if (hours <= 24) return `~${Math.ceil(hours)} hours at 50 emails/hour`;
                          const days = hours / 24;
                          return `~${Math.ceil(days)} days at 50 emails/hour (1,200/day cap)`;
                        })()}
                      </span>
                    )}
                  </span>
                )}
              </div>
              <Button onClick={handleLaunch} disabled={launching} className="w-full" data-testid="launch-campaign-btn">
                {launching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                Launch Campaign
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="space-y-2 py-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-slate-700">Sequence</h3>
            {editing && (
              <span className="text-[11px] text-slate-500">
                {bookingUrl ? (
                  <>Booking link: <code className="text-blue-700">{bookingUrl}</code></>
                ) : (
                  <span className="text-amber-600">
                    No booking link configured —{" "}
                    <a href="/calendar" className="underline">set one up</a>
                  </span>
                )}
              </span>
            )}
          </div>

          {(editing ? editSteps : campaign.steps || []).map((s, idx) => (
            <div
              key={s.order}
              className="border border-slate-200 rounded-md p-3"
              data-testid={`step-card-${s.order}`}
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-2 text-sm font-medium flex-1 min-w-0">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold flex-shrink-0">
                    {s.order}
                  </span>
                  {editing ? (
                    <Input
                      value={s.subject}
                      onChange={(e) => updateStep(idx, { subject: e.target.value })}
                      placeholder="Subject"
                      className="h-8 text-sm"
                      data-testid={`edit-step-subject-${s.order}`}
                    />
                  ) : (
                    <span className="truncate">{s.subject}</span>
                  )}
                </div>
                {editing ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Input
                      type="number"
                      min={0}
                      value={s.delay_days}
                      onChange={(e) =>
                        updateStep(idx, { delay_days: Math.max(0, parseInt(e.target.value || "0", 10)) })
                      }
                      className="w-16 h-8 text-xs"
                      data-testid={`edit-step-delay-${s.order}`}
                    />
                    <span className="text-[10px] text-slate-500">days</span>
                  </div>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    {s.delay_days === 0 ? "Day 0" : `+${s.delay_days}d`}
                  </Badge>
                )}
              </div>

              {editing ? (
                <div className="space-y-1">
                  <Textarea
                    value={s.body}
                    onChange={(e) => updateStep(idx, { body: e.target.value })}
                    rows={6}
                    className="text-xs font-mono"
                    placeholder="Body — use merge tags like {{contact_name}}, {{business_name}}, {{booking_link}}"
                    data-testid={`edit-step-body-${s.order}`}
                  />
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => insertBookingTag(idx)}
                      className="text-[11px] text-blue-600 hover:underline"
                      data-testid={`insert-booking-tag-${s.order}`}
                    >
                      + Insert booking link
                    </button>
                    <span className="text-[10px] text-slate-400">
                      {s.body.includes("{{booking_link}}") && "✓ contains booking link"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500 line-clamp-2">{s.body}</p>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="flex-row gap-2 justify-between">
          <Button variant="ghost" className="text-red-600" onClick={handleDelete} data-testid="delete-campaign-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
          <div className="flex gap-2">
            {editing && (
              <>
                <Button variant="outline" onClick={() => setEditing(false)} disabled={saving} data-testid="cancel-edit-btn">
                  Cancel
                </Button>
                <Button onClick={saveDraft} disabled={saving} data-testid="save-draft-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save changes
                </Button>
              </>
            )}
            {!editing && campaign.status === "active" && (
              <Button variant="outline" onClick={handlePause}><Pause className="w-4 h-4 mr-2" />Pause</Button>
            )}
            {!editing && campaign.status === "paused" && (
              <Button onClick={handleResume}><Play className="w-4 h-4 mr-2" />Resume</Button>
            )}
            {!editing && (
              <Button variant="outline" onClick={onClose}>Close</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-md p-3 text-center">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
